import { useState, useEffect } from "react";
import api from "../lib/api";
import { useWorkspace } from "../lib/workspaceContext";
import { Target, Award, TrendingUp, CheckCircle2, Plus, Trash2, Flame, Zap, Activity } from "lucide-react";
import { toast } from "sonner";

const CAT_META = {
  post: { color: "#CCFF00", icon: Activity, label: "Post" },
  engagement: { color: "#00D4FF", icon: TrendingUp, label: "Engage" },
  warmup: { color: "#F59E0B", icon: Flame, label: "Warm" },
  analysis: { color: "#A78BFA", icon: Target, label: "Analyse" },
  test: { color: "#EC4899", icon: Zap, label: "Test" },
};

export default function CommandCenter() {
  const [tab, setTab] = useState("missions");
  return (
    <div className="space-y-8 animate-fadeUp" data-testid="command-center-page">
      <header className="border-l-2 border-[#CCFF00] pl-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// COMMAND CENTER</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Missions, Objectifs & Santé</h1>
        <p className="text-zinc-400 mt-2 max-w-2xl">Le pulse opérationnel de ton workspace : missions du jour gamifiées, objectifs trackés, score de santé du compte.</p>
      </header>

      <div className="border-b border-[#1E1E24] flex gap-0 overflow-x-auto scrollbar-thin">
        {[
          { id: "missions", label: "Missions du jour", icon: CheckCircle2 },
          { id: "goals", label: "Objectifs", icon: Target },
          { id: "health", label: "Health Score", icon: Activity },
          { id: "feed", label: "Strategy Feed", icon: Zap },
        ].map(t => {
          const I = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`cc-tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-mono uppercase tracking-wider transition border-b-2 whitespace-nowrap ${
                tab === t.id ? "text-[#CCFF00] border-[#CCFF00]" : "text-zinc-500 border-transparent hover:text-zinc-300"
              }`}>
              <I className="w-4 h-4" strokeWidth={1.5} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "missions" && <MissionsTab />}
      {tab === "goals" && <GoalsTab />}
      {tab === "health" && <HealthTab />}
      {tab === "feed" && <StrategyFeedTab />}
    </div>
  );
}

// ============== MISSIONS ==============
function MissionsTab() {
  const { currentId } = useWorkspace();
  const [missions, setMissions] = useState([]);
  const [streak, setStreak] = useState(0);

  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    const all = await api.listMissions();
    const filtered = all.filter(m => (currentId ? m.workspace_id === currentId : true));
    setMissions(filtered);
    // calc streak: count past consecutive days where all missions done
    const byDate = {};
    filtered.forEach(m => { (byDate[m.date] = byDate[m.date] || []).push(m); });
    const dates = Object.keys(byDate).sort().reverse();
    let s = 0;
    for (const d of dates) {
      if (byDate[d].every(m => m.completed)) s++;
      else if (d !== today) break;
    }
    setStreak(s);
  };

  useEffect(() => { load(); }, [currentId]);

  const todayMissions = missions.filter(m => m.date === today);
  const done = todayMissions.filter(m => m.completed).length;
  const totalPoints = todayMissions.filter(m => m.completed).reduce((s, m) => s + m.points, 0);
  const maxPoints = todayMissions.reduce((s, m) => s + m.points, 0);

  const seed = async () => {
    await api.seedTodayMissions(currentId);
    toast.success("Missions du jour générées");
    load();
  };

  const toggle = async (m) => {
    await api.updateMission(m.id, { completed: !m.completed });
    load();
  };

  const del = async (id) => { await api.deleteMission(id); load(); };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        <Stat label="Streak (jours d'affilée)" value={streak} suffix="🔥" color="#F59E0B" />
        <Stat label="Aujourd'hui" value={`${done}/${todayMissions.length}`} color="#CCFF00" />
        <Stat label="Points du jour" value={`${totalPoints}/${maxPoints}`} color="#00D4FF" />
      </div>

      {todayMissions.length === 0 ? (
        <div className="border border-dashed border-[#1E1E24] rounded-sm p-12 text-center space-y-3">
          <CheckCircle2 className="w-10 h-10 text-zinc-700 mx-auto" strokeWidth={1.2} />
          <div className="text-zinc-500 text-sm">Aucune mission pour aujourd'hui.</div>
          <button onClick={seed} data-testid="seed-missions-btn"
            className="bg-[#CCFF00] text-black px-5 py-2 rounded-sm hover:bg-[#B3E600] text-sm font-medium">
            Générer 7 missions du jour
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {todayMissions.map(m => {
            const meta = CAT_META[m.category] || CAT_META.engagement;
            const Icon = meta.icon;
            return (
              <button key={m.id} onClick={() => toggle(m)} data-testid={`mission-${m.id}`}
                className={`w-full text-left flex items-center gap-4 px-4 py-3 rounded-sm border transition-all ${
                  m.completed ? "border-[#10B981]/30 bg-[#10B981]/5 opacity-60" : "border-[#1E1E24] hover:border-white/30 bg-[#0A0A0C]"
                }`}>
                <CheckCircle2 className={`w-5 h-5 shrink-0 transition ${m.completed ? "text-[#10B981] fill-[#10B981]" : "text-zinc-700"}`} strokeWidth={1.5} />
                <div className="w-7 h-7 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${meta.color}15`, border: `1px solid ${meta.color}40` }}>
                  <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} strokeWidth={1.5} />
                </div>
                <div className={`flex-1 text-sm ${m.completed ? "line-through text-zinc-500" : "text-white"}`}>{m.title}</div>
                <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: meta.color }}>+{m.points}</span>
                <button onClick={(e) => { e.stopPropagation(); del(m.id); }} className="text-zinc-700 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============== GOALS ==============
function GoalsTab() {
  const { currentId } = useWorkspace();
  const [goals, setGoals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ workspace_id: currentId, title: "", metric: "reach", target_value: 1000000, current_value: 0, deadline: "", status: "active" });

  const load = async () => {
    const all = await api.listGoals();
    setGoals(all.filter(g => (currentId ? g.workspace_id === currentId : true)));
  };
  useEffect(() => { load(); setForm(f => ({ ...f, workspace_id: currentId })); }, [currentId]);

  const submit = async (e) => {
    e.preventDefault();
    await api.createGoal({ ...form, workspace_id: currentId });
    toast.success("Objectif créé");
    setShowForm(false);
    setForm({ workspace_id: currentId, title: "", metric: "reach", target_value: 1000000, current_value: 0, deadline: "", status: "active" });
    load();
  };

  const del = async (id) => { await api.deleteGoal(id); load(); };
  const updateProgress = async (g, val) => { await api.updateGoal(g.id, { current_value: val }); load(); };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <p className="text-sm text-zinc-400">Tes objectifs trackés. L'IA détecte automatiquement quand tu approches.</p>
        <button onClick={() => setShowForm(s => !s)} data-testid="add-goal-btn"
          className="bg-[#CCFF00] text-black px-4 py-2 rounded-sm hover:bg-[#B3E600] flex items-center gap-2 text-sm">
          <Plus className="w-4 h-4" /> Objectif
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="goal-form">
          <input required placeholder="Titre (ex: 1M vues en 30j)" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white md:col-span-2" />
          <select value={form.metric} onChange={e => setForm({ ...form, metric: e.target.value })} className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
            <option value="reach">Reach</option><option value="followers">Followers</option>
            <option value="engagement">Engagement %</option><option value="watchtime">Watchtime %</option>
            <option value="posts">Posts publiés</option>
          </select>
          <input type="number" placeholder="Cible" value={form.target_value} onChange={e => setForm({ ...form, target_value: +e.target.value })} className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white" />
          <input type="number" placeholder="Actuel" value={form.current_value} onChange={e => setForm({ ...form, current_value: +e.target.value })} className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white" />
          <input type="date" value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white" />
          <button type="submit" data-testid="submit-goal" className="bg-[#CCFF00] text-black px-5 py-2 rounded-sm font-medium text-sm md:col-span-2">Enregistrer</button>
        </form>
      )}

      {goals.length === 0 ? (
        <div className="border border-dashed border-[#1E1E24] rounded-sm p-12 text-center text-zinc-500 text-sm">
          Aucun objectif. Crée le premier.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {goals.map(g => {
            const pct = g.target_value ? Math.min(100, Math.round((g.current_value / g.target_value) * 100)) : 0;
            return (
              <div key={g.id} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5" data-testid={`goal-${g.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-white font-medium">{g.title}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-1">
                      {g.metric} · {g.deadline || "pas de deadline"}
                    </div>
                  </div>
                  <button onClick={() => del(g.id)} className="text-zinc-600 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-white">{g.current_value.toLocaleString("fr-FR")} / {g.target_value.toLocaleString("fr-FR")}</span>
                    <span className="text-[#CCFF00]">{pct}%</span>
                  </div>
                  <div className="h-2 bg-[#1E1E24] rounded-full overflow-hidden">
                    <div className="h-full bg-[#CCFF00] transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <input type="number" value={g.current_value} onChange={e => updateProgress(g, +e.target.value)}
                    className="w-full bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-2 py-1 text-xs text-white text-center mt-2" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============== HEALTH SCORE ==============
function HealthTab() {
  const [accounts, setAccounts] = useState([]);
  const [kpis, setKpis] = useState([]);

  useEffect(() => {
    (async () => {
      const [a, k] = await Promise.all([api.listAccounts(), api.listKpis()]);
      setAccounts(a); setKpis(k);
    })();
  }, []);

  const computeHealth = (acc) => {
    const accKpis = kpis.filter(k => k.account_id === acc.id);
    if (accKpis.length === 0) return { score: 50, factors: [] };
    const avgEngagement = accKpis.reduce((s, k) => s + k.engagement_rate, 0) / accKpis.length;
    const avgWatchtime = accKpis.reduce((s, k) => s + k.watchtime_pct, 0) / accKpis.length;
    const sorted = [...accKpis].sort((a, b) => a.date.localeCompare(b.date));
    const recentReach = sorted.slice(-3).reduce((s, k) => s + k.reach, 0) / 3;
    const oldReach = sorted.slice(0, 3).reduce((s, k) => s + k.reach, 0) / 3;
    const growthPct = oldReach ? ((recentReach - oldReach) / oldReach) * 100 : 0;

    const f_eng = Math.min(100, (avgEngagement / 8) * 100);
    const f_wt = Math.min(100, (avgWatchtime / 70) * 100);
    const f_growth = Math.min(100, Math.max(0, 50 + growthPct));
    const f_warmup = acc.warmup_status === "actif" ? 100 : acc.warmup_status === "warmup" ? 70 : acc.warmup_status === "shadowban" ? 10 : 50;

    const score = Math.round((f_eng + f_wt + f_growth + f_warmup) / 4);
    return {
      score,
      factors: [
        { label: "Engagement", value: avgEngagement.toFixed(2) + "%", score: Math.round(f_eng) },
        { label: "Watchtime", value: avgWatchtime.toFixed(1) + "%", score: Math.round(f_wt) },
        { label: "Growth 14j", value: (growthPct >= 0 ? "+" : "") + growthPct.toFixed(1) + "%", score: Math.round(f_growth) },
        { label: "Warmup status", value: acc.warmup_status, score: f_warmup },
      ]
    };
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {accounts.map(acc => {
        const h = computeHealth(acc);
        const color = h.score >= 80 ? "#10B981" : h.score >= 60 ? "#CCFF00" : h.score >= 40 ? "#F59E0B" : "#EF4444";
        return (
          <div key={acc.id} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5 space-y-4" data-testid={`health-${acc.label}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-white font-medium">{acc.label}</div>
                <div className="text-[10px] font-mono text-zinc-500">{acc.handle}</div>
              </div>
              <div className="font-mono text-4xl font-bold" style={{ color }}>{h.score}</div>
            </div>
            <div className="space-y-2">
              {h.factors.map((f, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-[11px] font-mono">
                    <span className="text-zinc-300 uppercase tracking-wider">{f.label}</span>
                    <span className="text-white">{f.value}</span>
                  </div>
                  <div className="h-1 bg-[#1E1E24] rounded-full overflow-hidden mt-1">
                    <div className="h-full transition-all" style={{ width: `${f.score}%`, background: f.score >= 80 ? "#10B981" : f.score >= 60 ? "#CCFF00" : f.score >= 40 ? "#F59E0B" : "#EF4444" }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============== STRATEGY FEED ==============
function StrategyFeedTab() {
  const [accounts, setAccounts] = useState([]);
  const [kpis, setKpis] = useState([]);

  useEffect(() => {
    (async () => {
      const [a, k] = await Promise.all([api.listAccounts(), api.listKpis()]);
      setAccounts(a); setKpis(k);
    })();
  }, []);

  // Generate rule-based insights
  const insights = [];
  accounts.forEach(acc => {
    const accKpis = kpis.filter(k => k.account_id === acc.id);
    if (accKpis.length < 3) return;
    const sorted = [...accKpis].sort((a, b) => a.date.localeCompare(b.date));
    const last3 = sorted.slice(-3);
    const prev3 = sorted.slice(-6, -3);
    const recentReach = last3.reduce((s, k) => s + k.reach, 0) / 3;
    const prevReach = prev3.length ? prev3.reduce((s, k) => s + k.reach, 0) / 3 : recentReach;
    const recentWt = last3.reduce((s, k) => s + k.watchtime_pct, 0) / 3;
    const recentEng = last3.reduce((s, k) => s + k.engagement_rate, 0) / 3;

    if (prevReach && (recentReach - prevReach) / prevReach < -0.2) {
      insights.push({ severity: "warning", icon: "TrendingDown", title: `Chute reach sur ${acc.label}`, body: `Reach -${Math.round(((prevReach - recentReach) / prevReach) * 100)}% sur les 3 derniers jours. Vérifie hashtags + watermarks + watchtime.` });
    }
    if (prevReach && (recentReach - prevReach) / prevReach > 0.5) {
      insights.push({ severity: "success", icon: "TrendingUp", title: `Push fort sur ${acc.label}`, body: `Reach +${Math.round(((recentReach - prevReach) / prevReach) * 100)}% — capitalise vite avec un follow-up dans les 24h.` });
    }
    if (recentWt < 50) {
      insights.push({ severity: "warning", icon: "Eye", title: `Watchtime faible sur ${acc.label}`, body: `${recentWt.toFixed(1)}% < 60% min. Renforce hook 0-1s, raccourcis vidéo, ajoute loop invisible.` });
    }
    if (recentEng > 8) {
      insights.push({ severity: "success", icon: "Heart", title: `Engagement élevé sur ${acc.label}`, body: `${recentEng.toFixed(2)}% — audience hot. Lance un comment-bait pour cascader.` });
    }
    if (acc.warmup_status === "shadowban") {
      insights.push({ severity: "danger", icon: "ShieldAlert", title: `Shadowban suspecté ${acc.label}`, body: "Stop posts 7-14j, désactive le compte 3j si possible, repars en warm-up doux." });
    }
  });

  // Add generic time-based insights
  const hour = new Date().getHours();
  if (hour >= 17 && hour <= 22) {
    insights.push({ severity: "info", icon: "Clock", title: "Prime time démarre", body: "18-22h = pic d'audience pour la majorité des niches FR. Idéal pour publier maintenant." });
  }

  if (insights.length === 0) {
    insights.push({ severity: "info", icon: "Sparkles", title: "Tout est stable", body: "Aucune anomalie majeure détectée. Continue à exécuter ta routine quotidienne." });
  }

  const sevColor = { success: "#10B981", info: "#00D4FF", warning: "#F59E0B", danger: "#EF4444" };

  return (
    <div className="space-y-3">
      {insights.map((insight, i) => (
        <div key={i} className="border bg-[#0A0A0C] rounded-sm p-5 flex items-start gap-4"
          style={{ borderColor: `${sevColor[insight.severity]}40` }} data-testid={`feed-${i}`}>
          <div className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${sevColor[insight.severity]}15`, border: `1px solid ${sevColor[insight.severity]}40` }}>
            <Activity className="w-4 h-4" style={{ color: sevColor[insight.severity] }} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="text-white font-medium text-sm">{insight.title}</div>
            <div className="text-zinc-400 text-sm mt-1 leading-relaxed">{insight.body}</div>
          </div>
          <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm" style={{ color: sevColor[insight.severity], background: `${sevColor[insight.severity]}10`, border: `1px solid ${sevColor[insight.severity]}40` }}>
            {insight.severity}
          </span>
        </div>
      ))}
    </div>
  );
}

const Stat = ({ label, value, suffix, color }) => (
  <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5">
    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-2">{label}</div>
    <div className="font-mono text-3xl font-medium" style={{ color }}>{value}{suffix && <span className="ml-1">{suffix}</span>}</div>
  </div>
);
